import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, isWoo, clearWooSession } from '../lib/auth';

// =============================================================================
// APP BRIDGE TYPES
// =============================================================================

declare global {
  interface Window {
    shopify?: {
      idToken(): Promise<string>;
      toast: { show(msg: string, opts?: { duration?: number; isError?: boolean }): void };
      redirectTo(url: string): void;
    };
  }
}

// =============================================================================
// API CLIENT
// =============================================================================

const API_BASE = '/api';

// Dedup concurrent token-exchange POSTs: the dashboard fires many queries at
// mount and each would otherwise trigger its own provisioning call.
let tokenExchangePromise: Promise<boolean> | null = null;

/**
 * Provision the Shop row via the token-exchange endpoint (Shopify only).
 * Under Managed Installation the embedded SPA loads BEFORE a Shop row exists,
 * so the first API call 404s "Shop not found". We exchange the App Bridge
 * session JWT for an offline access token; after that the row is persisted and
 * subsequent calls succeed. Returns true when provisioning succeeded.
 */
async function provisionShopViaTokenExchange(): Promise<boolean> {
  if (tokenExchangePromise) return tokenExchangePromise;
  tokenExchangePromise = (async () => {
    try {
      const token = await window.shopify?.idToken();
      if (!token) return false;
      const res = await fetch(`${API_BASE}/auth/token-exchange`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Some reverse proxies strip Authorization; X-Session-Token survives.
          'X-Session-Token': token,
        },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      tokenExchangePromise = null;
    }
  })();
  return tokenExchangePromise;
}

/** API error that preserves the response's machine-readable fields. */
export class ApiError extends Error {
  status: number;
  code?: string;
  feature?: string;
  currentPlan?: string;
  requiredPlan?: string;

  constructor(message: string, status: number, body: Record<string, unknown> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (typeof body.code === 'string') this.code = body.code;
    if (typeof body.feature === 'string') this.feature = body.feature;
    if (typeof body.currentPlan === 'string') this.currentPlan = body.currentPlan;
    if (typeof body.requiredPlan === 'string') this.requiredPlan = body.requiredPlan;
  }
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit & { _retried?: boolean }
): Promise<T> {
  // Shopify -> Bearer id token; WooCommerce -> httpOnly session cookie.
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include', // send the WC session cookie; harmless for Shopify
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    // An expired/invalid WC session token must not be re-sent forever: clear it so
    // the next full load (from WP-admin, carrying a fresh SSO token) re-bootstraps.
    if (response.status === 401 && isWoo()) {
      clearWooSession();
    }
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    const errMsg = error.error || error.message || 'Request failed';

    // Shopify Managed Installation: the SPA loads before the Shop row exists, so
    // the first authenticated call 404s "Shop not found". Provision via token
    // exchange, then retry the original request ONCE (_retried guards the loop).
    if (!isWoo() && errMsg === 'Shop not found' && !options?._retried) {
      const provisioned = await provisionShopViaTokenExchange();
      if (provisioned) {
        return fetchApi<T>(endpoint, { ...options, _retried: true });
      }
    }

    // Keep the machine-readable gate fields (402 BILLING_REQUIRED / 403
    // FEATURE_NOT_AVAILABLE bodies carry code + requiredPlan) - a plain Error
    // dropped them, leaving gate banners with no way to offer an upgrade CTA.
    throw new ApiError(errMsg, response.status, error);
  }

  return response.json();
}

// =============================================================================
// DASHBOARD
// =============================================================================

interface DashboardStats {
  conversions: {
    thisMonth: number;
    lastMonth: number;
    growth: number;
  };
  revenue: {
    thisMonth: number;
    lastMonth: number;
  };
  currency: string;
  /** Share of conversions carrying any click id (attribution reach). */
  trackingRate: number;
  /** Share of conversions with no enabled channel still undelivered. */
  deliveryRate: number;
  failed: {
    thisMonth: number;
  };
  events: {
    thisMonth: number;
    limit: number;
  };
}

// How often the dashboard refreshes itself while the merchant is looking at it.
// It used to be never: this query had no interval, the QueryClient sets
// refetchOnWindowFocus:false globally, so it fetched once on mount and then sat
// there. A real order placed in another tab only appeared after a browser
// reload, which reads as "the tracking is not working" on the one screen where
// that impression is most expensive.
//
// Nothing in the pipeline was slow. Measured on order #1001 in production: the
// Conversion row was written 00:42:50.571Z, four seconds after the last funnel
// event, with googleAdsAt and ga4At stamped in the SAME write (the worker
// delivers to every channel, then upserts once). The whole wait was in the UI.
//
// 10s. The cost is bounded by attention rather than by installs: React Query
// pauses intervals in a background tab, so this is one query per 10s per VISIBLE
// dashboard, and a merchant who leaves the tab open all day pays nothing for it.
// What it buys is the minute that matters, the one where someone has just placed
// their first order and is watching this screen to decide whether the product
// works.
//
// It does not make an order appear the instant it is paid, and nothing can: the
// Conversion row is not written until delivery to every channel has finished.
// Below this the interval would just be re-asking during that window.
const DASHBOARD_POLL_MS = 10_000;

export function useDashboard() {
  return useQuery<DashboardStats>({
    queryKey: ['dashboard'],
    queryFn: () => fetchApi('/dashboard/stats'),
    refetchInterval: DASHBOARD_POLL_MS,
    // The global default is false. On this screen a merchant who places a test
    // order in another tab and comes back should not have to wait out the
    // interval, let alone reload.
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// CONVERSIONS
// =============================================================================

interface ConversionsResponse {
  conversions: Array<{
    id: string;
    orderId: string;
    orderName: string;
    revenue: number;
    currency: string;
    gclid: string | null;
    fbclid: string | null;
    ttclid: string | null;
    epik: string | null;
    sccid: string | null;
    googleAdsTracked: boolean;
    metaTracked: boolean;
    ga4Tracked: boolean;
    tiktokTracked: boolean;
    pinterestTracked: boolean;
    snapchatTracked: boolean;
    source: string;
    createdAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export function useConversions(
  limit = 10,
  options: { refetchInterval?: number | false } = {},
) {
  return useQuery<ConversionsResponse>({
    queryKey: ['dashboard', 'conversions', limit],
    queryFn: () => fetchApi(`/dashboard/conversions?limit=${limit}`),
    // The Live Event Debugger passes 5s (and false while paused). Everyone else
    // gets the dashboard cadence rather than nothing: this table is "Recent
    // Conversions", and a recent-conversions table that only updates on reload
    // is a bug wearing a feature's name. React Query skips the interval while
    // the tab is in the background.
    refetchInterval:
      options.refetchInterval === undefined ? DASHBOARD_POLL_MS : options.refetchInterval,
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// GOOGLE ADS
// =============================================================================

interface GoogleAdsConfig {
  connected: boolean;
  hasAccount?: boolean;
  hasConversionAction?: boolean;
  status: 'connected' | 'warning' | 'error' | 'disabled';
  customerId?: string;
  customerName?: string;
  isEnabled?: boolean;
  enhancedConversionsEnabled?: boolean;
  purchaseActionId?: string;
  purchaseActionName?: string;
  lastSyncAt?: string;
  lastSuccessAt?: string;
  lastErrorMessage?: string;
}

interface ConversionAction {
  id: string;
  name: string;
  status: string;
  type: string;
  category?: string;
  categoryName?: string;
  typeName?: string;
  /** Only UPLOAD_CLICKS actions can receive server-side uploads. */
  uploadEligible?: boolean;
  primaryForGoal?: boolean;
}

export interface EnhancedConversionsStatus {
  // false when the live Google Ads read failed; UI then shows neutral guidance.
  checked: boolean;
  acceptedCustomerDataTerms?: boolean;
  conversionTrackingStatus?: string;
}

export function useGoogleAdsConfig() {
  return useQuery<GoogleAdsConfig>({
    queryKey: ['google-ads', 'config'],
    queryFn: () => fetchApi('/google-ads/config'),
  });
}

// =============================================================================
// HISTORICAL BACKFILL HELD FROM THE AD PLATFORMS
// =============================================================================
// The install backfill captures 90 days of orders but holds them from Google
// Ads / Meta / TikTok / Pinterest / Snapchat until the merchant says so: those
// are their accounts and their attribution, and if they already ran another
// tool over the same period we would double their history. GA4 receives them
// immediately, which is why the dashboard has data before anyone decides.

export interface BackfillHoldStatus {
  held: number;
  releasedAt: string | null;
}

export function useBackfillHold() {
  return useQuery<BackfillHoldStatus>({
    queryKey: ['backfill', 'hold'],
    queryFn: () => fetchApi('/dashboard/backfill-hold'),
    // Nothing here changes without a deliberate act, so no polling.
    staleTime: 5 * 60 * 1000,
  });
}

export function useReleaseBackfillHold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchApi<{ success: boolean; released: number }>('/dashboard/backfill-hold/release', {
        method: 'POST',
      }),
    onSuccess: () => {
      // The banner must disappear, and the dashboard counters will move as the
      // released jobs land.
      queryClient.invalidateQueries({ queryKey: ['backfill', 'hold'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useGoogleAdsAuthUrl() {
  return useMutation({
    mutationFn: () => fetchApi<{ authUrl: string }>('/google-ads/auth-url', { method: 'POST' }),
  });
}

export interface GoogleAdsAccount {
  customerId: string;
  descriptiveName: string;
  currencyCode: string;
  timeZone: string;
  loginCustomerId: string | null;
}

export function useGoogleAdsAccounts() {
  // Fetched on demand (when the account picker opens) via refetch(), not on mount.
  return useQuery<{ accounts: GoogleAdsAccount[] }>({
    queryKey: ['google-ads', 'accounts'],
    queryFn: () => fetchApi('/google-ads/accounts'),
    enabled: false,
    retry: false,
  });
}

export function useSelectGoogleAdsAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (account: {
      customerId: string;
      customerName: string;
      currencyCode: string;
      loginCustomerId: string | null;
    }) =>
      fetchApi('/google-ads/select-account', {
        method: 'POST',
        body: JSON.stringify(account),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads'] });
    },
  });
}

export function useGoogleAdsConversionActions() {
  return useQuery<{ actions: ConversionAction[] }>({
    queryKey: ['google-ads', 'conversion-actions'],
    queryFn: () => fetchApi('/google-ads/conversion-actions'),
    enabled: false,
  });
}

// Advisory live read of the account's Enhanced Conversions terms acceptance.
// Only enabled when the merchant has turned on our EC flag, so we do not make a
// Google Ads round-trip on every page load.
export function useEnhancedConversionsStatus(enabled: boolean) {
  return useQuery<EnhancedConversionsStatus>({
    queryKey: ['google-ads', 'enhanced-conversions', 'status'],
    queryFn: () => fetchApi('/google-ads/enhanced-conversions/status'),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

// Full-funnel conversion actions (source-of-truth table + catalogue of supported
// event types so the UI can offer creation of the ones not yet configured).
export interface ConfiguredAction {
  eventType: string;
  resourceName: string;
  name: string;
  category: string;
  isPrimary: boolean;
  enabled: boolean;
}
export interface SupportedAction {
  eventType: string;
  name: string;
  category: string;
  isPrimary: boolean;
}

export function useConfiguredActions(enabled: boolean) {
  return useQuery<{ actions: ConfiguredAction[]; supported: SupportedAction[] }>({
    queryKey: ['google-ads', 'configured-actions'],
    queryFn: () => fetchApi('/google-ads/configured-actions'),
    enabled,
  });
}

export function useEnsureAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventType: string) =>
      fetchApi('/google-ads/ensure-action', {
        method: 'POST',
        body: JSON.stringify({ eventType }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads'] });
    },
  });
}

export function useToggleAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { eventType: string; enabled: boolean }) =>
      fetchApi('/google-ads/toggle-action', {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads'] });
    },
  });
}

export function useSelectConversionAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversionActionId: string) =>
      fetchApi('/google-ads/select-conversion-action', {
        method: 'POST',
        body: JSON.stringify({ conversionActionId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads'] });
    },
  });
}

export function useCreateConversionAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchApi('/google-ads/create-conversion-action', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads'] });
    },
  });
}

// Anti-double-counting: other PRIMARY purchase actions in the merchant's
// account (each one double counts purchases while it stays primary).
export interface PrimaryConflict {
  resourceName: string;
  name: string;
  categoryName: string;
}

export function useGoogleAdsConflicts(enabled: boolean) {
  return useQuery<{ conflicts: PrimaryConflict[] }>({
    queryKey: ['google-ads', 'conflicts'],
    queryFn: () => fetchApi('/google-ads/conflicts'),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFixPrimaryConflicts() {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; demoted: PrimaryConflict[] }>({
    mutationFn: () => fetchApi('/google-ads/fix-primary', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads', 'conflicts'] });
    },
  });
}

export function useRestorePrimary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resourceNames: string[]) =>
      fetchApi('/google-ads/restore-primary', {
        method: 'POST',
        body: JSON.stringify({ resourceNames }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads', 'conflicts'] });
    },
  });
}

export function useToggleGoogleAds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/google-ads/toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads'] });
    },
  });
}

export function useToggleEnhancedConversions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/google-ads/enhanced-conversions', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads'] });
    },
  });
}

export interface GoogleAdsTestResult {
  success: boolean;
  accepted?: boolean;
  advisory?: string;
  allowlistBlocked?: boolean;
  error?: string;
  testOrderId?: string;
  /** Google's Data Manager acknowledgment id - immediate proof of receipt. */
  requestId?: string;
}

export function useTestGoogleAds() {
  return useMutation<GoogleAdsTestResult>({
    mutationFn: () => fetchApi('/google-ads/test', { method: 'POST' }),
  });
}

export function useDisconnectGoogleAds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opts?: { removeActions?: boolean }) =>
      fetchApi('/google-ads/disconnect', {
        method: 'POST',
        body: JSON.stringify({ removeActions: opts?.removeActions === true }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads'] });
    },
  });
}

// =============================================================================
// META CAPI
// =============================================================================

interface MetaConfig {
  connected: boolean;
  hasPixel: boolean;
  status: 'connected' | 'warning' | 'error' | 'disabled';
  pixelId?: string;
  isEnabled?: boolean;
  testEventCode?: string;
  eventMatchQuality?: Record<string, number>;
  emqStatus?: {
    overall: string;
    color: string;
    recommendations: string[];
  };
  lastSyncAt?: string;
  lastSuccessAt?: string;
  lastErrorMessage?: string;
}

interface EMQData {
  available: boolean;
  overall?: string;
  scores?: Record<string, number>;
  recommendations?: string[];
}

export function useMetaConfig() {
  return useQuery<MetaConfig>({
    queryKey: ['meta', 'config'],
    queryFn: () => fetchApi('/meta/config'),
  });
}

export function useConfigureMeta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { pixelId: string; accessToken: string; testEventCode?: string }) =>
      fetchApi('/meta/configure', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
  });
}

export function useToggleMeta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/meta/toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
  });
}

export function useTestMeta() {
  return useMutation({
    mutationFn: () => fetchApi('/meta/test', { method: 'POST' }),
  });
}

export function useUpdateTestEventCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (testEventCode: string) =>
      fetchApi('/meta/test-event-code', {
        method: 'POST',
        body: JSON.stringify({ testEventCode }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
  });
}

export function useMetaEMQ() {
  return useQuery<EMQData>({
    queryKey: ['meta', 'emq'],
    queryFn: () => fetchApi('/meta/emq'),
    enabled: false,
  });
}

export function useRefreshEMQ() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/meta/refresh-emq', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta', 'emq'] });
    },
  });
}

export function useDisconnectMeta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/meta/disconnect', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
  });
}

// Meta one-click Connect (Facebook Login) - Shopify + WooCommerce.
export interface MetaPixelOption {
  pixelId: string;
  pixelName: string;
  adAccountName: string;
}

/** An ad account or Business we swept, so the empty state can name it. */
export interface MetaAssetOwner {
  name: string;
  source: 'ad_account' | 'business';
  pixelCount: number;
}

export interface MetaAssetsResponse {
  pixels: MetaPixelOption[];
  accounts: MetaAssetOwner[];
  /** Graph refused to enumerate: Tech Provider verification is still pending. */
  blocked: boolean;
}

export function useConnectMeta() {
  return useMutation<{ url: string }, Error>({
    mutationFn: () => fetchApi('/meta/auth'),
  });
}

export function useMetaAssets(enabled: boolean) {
  return useQuery<MetaAssetsResponse>({
    queryKey: ['meta', 'assets'],
    queryFn: () => fetchApi('/meta/assets'),
    enabled,
    retry: false,
  });
}

export function useSelectMetaAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { pixelId: string }) =>
      fetchApi('/meta/select-asset', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
  });
}

// =============================================================================
// GA4
// =============================================================================

interface GA4Config {
  connected: boolean;
  status?: 'connected' | 'warning' | 'error' | 'disabled';
  measurementId?: string;
  isEnabled?: boolean;
  lastSyncAt?: string;
  lastSuccessAt?: string;
  lastErrorMessage?: string;
}

export function useGA4Config() {
  return useQuery<GA4Config>({
    queryKey: ['ga4', 'config'],
    queryFn: () => fetchApi('/ga4/config'),
  });
}

export function useConfigureGA4() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { measurementId: string; apiSecret: string }) =>
      fetchApi('/ga4/configure', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ga4'] });
    },
  });
}

export function useToggleGA4() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/ga4/toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ga4'] });
    },
  });
}

export function useTestGA4() {
  return useMutation({
    mutationFn: () => fetchApi('/ga4/test', { method: 'POST' }),
  });
}

export function useDisconnectGA4() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/ga4/disconnect', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ga4'] });
    },
  });
}

// GA4 one-click Connect (OAuth) — works on Shopify and WooCommerce alike
// (unified verifySession + platform-aware callback redirect).
export interface GA4Stream {
  propertyId: string;
  propertyName: string;
  dataStreamId: string;
  streamName: string;
  measurementId: string;
}

export function useConnectGA4() {
  // GET /ga4/auth -> { url }; caller redirects the browser to start OAuth.
  return useMutation<{ url: string }, Error>({
    mutationFn: () => fetchApi('/ga4/auth'),
  });
}

export interface GA4Account { name: string; displayName: string }

export function useGA4Assets(enabled: boolean) {
  return useQuery<{ streams: GA4Stream[]; accounts: GA4Account[] }>({
    queryKey: ['ga4', 'assets'],
    queryFn: () => fetchApi('/ga4/assets'),
    enabled,
    retry: false,
  });
}

export function useCreateGA4Property() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { accountName: string }) =>
      fetchApi<{ success: boolean; propertyName: string; measurementId: string }>(
        '/ga4/create-property',
        { method: 'POST', body: JSON.stringify(data) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ga4'] });
    },
  });
}

export function useSelectGA4Asset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { dataStreamId: string; measurementId: string }) =>
      fetchApi('/ga4/select-asset', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ga4'] });
    },
  });
}

// =============================================================================
// TIKTOK
// =============================================================================

interface TikTokConfig {
  connected: boolean;
  status?: 'connected' | 'warning' | 'error' | 'disabled';
  // Whether OAuth pixel-discovery is available (TikTok Marketing API app is
  // configured). When false, the page shows the manual pixel + token setup only.
  oauthAvailable?: boolean;
  pixelCode?: string;
  isEnabled?: boolean;
  testEventCode?: string;
  lastSyncAt?: string;
  lastSuccessAt?: string;
  lastErrorMessage?: string;
}

export function useTikTokConfig() {
  return useQuery<TikTokConfig>({
    queryKey: ['tiktok', 'config'],
    queryFn: () => fetchApi('/tiktok/config'),
  });
}

export function useConfigureTikTok() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { pixelCode: string; accessToken: string; testEventCode?: string }) =>
      fetchApi('/tiktok/configure', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok'] });
    },
  });
}

export function useToggleTikTok() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/tiktok/toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok'] });
    },
  });
}

export function useTestTikTok() {
  return useMutation({
    mutationFn: () => fetchApi('/tiktok/test', { method: 'POST' }),
  });
}

export function useDisconnectTikTok() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/tiktok/disconnect', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiktok'] });
    },
  });
}

// TikTok Connect (OAuth) - discovers pixels only; the Events API token is still
// pasted (TikTok platform limitation). No select-asset; the picker pre-fills the
// pixel code into the manual config form.
export interface TikTokPixelOption {
  pixelCode: string;
  name: string;
  advertiserName: string;
}

export function useConnectTikTok() {
  return useMutation<{ url: string }, Error>({
    mutationFn: () => fetchApi('/tiktok/auth'),
  });
}

export function useTikTokAssets(enabled: boolean) {
  return useQuery<{ pixels: TikTokPixelOption[] }>({
    queryKey: ['tiktok', 'assets'],
    queryFn: () => fetchApi('/tiktok/assets'),
    enabled,
    retry: false,
  });
}

// =============================================================================
// PINTEREST
// =============================================================================

interface PinterestConfig {
  connected: boolean;
  // Whether one-click Connect is available (the Pinterest developer app is
  // configured). When false the page offers the manual setup only, instead
  // of sending the merchant to Pinterest with an empty client_id.
  oauthAvailable?: boolean;
  status?: 'connected' | 'warning' | 'error' | 'disabled';
  adAccountId?: string;
  isEnabled?: boolean;
  lastSyncAt?: string;
  lastSuccessAt?: string;
  lastErrorMessage?: string;
}

export function usePinterestConfig() {
  return useQuery<PinterestConfig>({
    queryKey: ['pinterest', 'config'],
    queryFn: () => fetchApi('/pinterest/config'),
  });
}

export function useConfigurePinterest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { adAccountId: string; conversionToken: string }) =>
      fetchApi('/pinterest/configure', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pinterest'] });
    },
  });
}

export function useTogglePinterest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/pinterest/toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pinterest'] });
    },
  });
}

export function useTestPinterest() {
  return useMutation({
    mutationFn: () => fetchApi('/pinterest/test', { method: 'POST' }),
  });
}

export function useDisconnectPinterest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/pinterest/disconnect', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pinterest'] });
    },
  });
}

// Pinterest one-click Connect (OAuth) - Shopify + WooCommerce.
export interface PinterestAdAccountOption {
  adAccountId: string;
  name: string;
}

export function useConnectPinterest() {
  return useMutation<{ url: string }, Error>({
    mutationFn: () => fetchApi('/pinterest/auth'),
  });
}

export function usePinterestAssets(enabled: boolean) {
  return useQuery<{ accounts: PinterestAdAccountOption[] }>({
    queryKey: ['pinterest', 'assets'],
    queryFn: () => fetchApi('/pinterest/assets'),
    enabled,
    retry: false,
  });
}

export function useSelectPinterestAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { adAccountId: string }) =>
      fetchApi('/pinterest/select-asset', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pinterest'] });
    },
  });
}

// =============================================================================
// SNAPCHAT
// =============================================================================

interface SnapchatConfig {
  connected: boolean;
  // Whether one-click Connect is available (the Snapchat developer app is
  // configured). When false the page offers the manual setup only, instead
  // of sending the merchant to Snapchat with an empty client_id.
  oauthAvailable?: boolean;
  status?: 'connected' | 'warning' | 'error' | 'disabled';
  pixelId?: string;
  isEnabled?: boolean;
  testEventCode?: string;
  lastSyncAt?: string;
  lastSuccessAt?: string;
  lastErrorMessage?: string;
}

export function useSnapchatConfig() {
  return useQuery<SnapchatConfig>({
    queryKey: ['snapchat', 'config'],
    queryFn: () => fetchApi('/snapchat/config'),
  });
}

export function useConfigureSnapchat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { pixelId: string; accessToken: string; testEventCode?: string }) =>
      fetchApi('/snapchat/configure', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['snapchat'] });
    },
  });
}

export function useToggleSnapchat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/snapchat/toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['snapchat'] });
    },
  });
}

export function useTestSnapchat() {
  return useMutation({
    mutationFn: () => fetchApi('/snapchat/test', { method: 'POST' }),
  });
}

export function useDisconnectSnapchat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/snapchat/disconnect', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['snapchat'] });
    },
  });
}

// Snapchat one-click Connect (OAuth) - Shopify + WooCommerce.
export interface SnapchatPixelOption {
  pixelId: string;
  pixelName: string;
  adAccountName: string;
  orgName: string;
}

/** What the merchant's token could actually see, pixel or not. */
export interface SnapchatAdAccountOption {
  orgName: string;
  adAccountName: string;
  pixelCount: number;
}

export function useConnectSnapchat() {
  return useMutation<{ url: string }, Error>({
    mutationFn: () => fetchApi('/snapchat/auth'),
  });
}

export function useSnapchatAssets(enabled: boolean) {
  return useQuery<{ pixels: SnapchatPixelOption[]; accounts: SnapchatAdAccountOption[] }>({
    queryKey: ['snapchat', 'assets'],
    queryFn: () => fetchApi('/snapchat/assets'),
    enabled,
    retry: false,
  });
}

export function useSelectSnapchatAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { pixelId: string }) =>
      fetchApi('/snapchat/select-asset', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['snapchat'] });
    },
  });
}

// =============================================================================
// OUTBOUND WEBHOOKS
// =============================================================================

interface WebhookConfigResponse {
  connected: boolean;
  status?: 'connected' | 'warning' | 'error' | 'disabled';
  url?: string;
  isEnabled?: boolean;
  events?: string[];
  lastSentAt?: string;
  lastErrorAt?: string;
  lastErrorMessage?: string;
  consecutiveErrors?: number;
  createdAt?: string;
}

export function useWebhookConfig() {
  return useQuery<WebhookConfigResponse>({
    queryKey: ['webhooks-config', 'config'],
    queryFn: () => fetchApi('/webhooks-config/config'),
  });
}

export function useConfigureWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { url: string; events?: string[] }) =>
      fetchApi<{ success: boolean; secret: string; message: string }>('/webhooks-config/configure', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks-config'] });
    },
  });
}

export function useToggleWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/webhooks-config/toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks-config'] });
    },
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: () => fetchApi('/webhooks-config/test', { method: 'POST' }),
  });
}

export function useDisconnectWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/webhooks-config/disconnect', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks-config'] });
    },
  });
}

// =============================================================================
// SETTINGS
// =============================================================================

interface ShopSettings {
  id: string;
  shopDomain: string;
  shopName: string;
  isActive: boolean;
  defaultConsent: {
    adStorage: string;
    analyticsStorage: string;
    adUserData: string;
    adPersonalization: string;
  };
  createdAt: string;
}

export function useShopSettings() {
  return useQuery<ShopSettings>({
    queryKey: ['settings'],
    queryFn: () => fetchApi('/dashboard/settings'),
  });
}

export function useUpdateConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (consent: ShopSettings['defaultConsent']) =>
      fetchApi('/dashboard/consent', {
        method: 'POST',
        body: JSON.stringify(consent),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

// =============================================================================
// ONBOARDING (Phase 4 - "all green, we handle the rest")
// =============================================================================

export interface ChannelStatus {
  connected: boolean;
  enabled: boolean;
  healthy: boolean;
  configured?: boolean; // Google Ads only: account + purchase action present
  issues?: string[];
}

/** A conversion pixel other than ours, detected on the storefront. */
export interface CompetingSource {
  id: string;
  label: string;
  channel: string;
  /** EVENT_ID = merchant can dedup by passing our event id. NONE = they must run one source. */
  dedup: 'EVENT_ID' | 'ORDER_ID' | 'NONE';
  lastSeenAt: string;
}

export interface CompetingPixels {
  sources: CompetingSource[];
  dedupable: string[];
  /** Sources whose calls we already stamp with our event id: nothing to do. */
  alreadyDeduped: string[];
  mustDisable: string[];
  selfDeduping: string[];
  lastSeenAt: string | null;
}

export type FunnelChannelKey = 'googleAds' | 'meta' | 'ga4' | 'tiktok' | 'pinterest' | 'snapchat';

/**
 * Per channel: does the funnel forward, and on which proof. Decided entirely
 * server-side (services/funnel/eligibility.ts). `reason` is a CODE - the UI maps
 * it to a translated sentence and never decides the outcome itself.
 */
export interface FunnelDecision {
  forward: boolean;
  proof: 'OWN_DESTINATION' | 'DEDUP_KEY' | 'EVIDENCE_EMPTY' | 'MERCHANT_DECLARED' | null;
  reason:
    | 'OWN_CONVERSION_ACTIONS' | 'BROWSER_TAG_STAMPED' | 'DESTINATION_NOT_FED'
    | 'MERCHANT_SAYS_NO_OTHER_TAG' | 'COMPETING_TAG_NOT_STAMPED'
    | 'MERCHANT_KEEPS_THEIR_TAG' | 'DESTINATION_ALREADY_FED' | 'UNVERIFIED';
  sources: string[];
}

export interface OnboardingStatus {
  active: boolean;
  pixelEmitting: boolean;
  /** Theme app embed heartbeat (Shopify): true once the storefront tracker has pinged. */
  clickTrackerActive: boolean;
  clickTrackerLastSeenAt: string | null;
  /** Theme-editor deep link that opens with the embed pre-activated (Shopify). */
  themeEditorUrl: string | null;
  platform: 'SHOPIFY' | 'WOOCOMMERCE';
  onboardingCompleted: boolean;
  funnelEnabled: boolean;
  liveChannelCount: number;
  liveChannels: string[];
  // Monetization without data loss: forwarding to ad platforms is paused
  // (over quota + unentitled). Data is still captured; upgrading resumes + backfills.
  forwardingPaused: boolean;
  quotaPausedCount: number;
  /** Browser pixels running alongside our server-side sending (double-count risk). */
  competingPixels?: CompetingPixels;
  /** Per-channel funnel decision, or null if the recompute failed this poll. */
  funnelRouting?: Record<FunnelChannelKey, FunnelDecision> | null;
  /** What the merchant answered per channel. A missing key means never asked. */
  funnelDeclaredTags?: Partial<Record<FunnelChannelKey, boolean>> | null;
  channels: {
    googleAds: ChannelStatus;
    meta: ChannelStatus;
    ga4: ChannelStatus;
    tiktok: ChannelStatus;
    pinterest: ChannelStatus;
    snapchat: ChannelStatus;
  };
}

export function useOnboardingStatus() {
  return useQuery<OnboardingStatus>({
    queryKey: ['onboarding', 'status'],
    queryFn: () => fetchApi('/onboarding/status'),
    // Poll fast (20s) while the merchant is still setting up OR forwarding is
    // paused, so the card flips to green (and the paused banner clears after an
    // upgrade) promptly. Once active AND unpaused there is nothing imminent to
    // catch, so back off to 5 min: no reason to hit the DB every 20s forever for
    // every open dashboard tab. A later channel-health drop or quota pause is
    // still picked up within 5 min (and immediately on window focus / a
    // mutation's cache invalidation).
    refetchInterval: (query) => {
      const d = query.state.data as OnboardingStatus | undefined;
      return d && d.active && !d.forwardingPaused ? 300_000 : 20_000;
    },
    // The global default is refetchOnWindowFocus:false. Re-enable it just for
    // this query so a backed-off (5-min) tab still catches a server-side change
    // (channel health drop, quota pause) the moment the merchant refocuses -
    // the responsiveness net the back-off relies on.
    refetchOnWindowFocus: true,
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/onboarding/complete', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

export function useToggleFunnel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      fetchApi('/onboarding/funnel', { method: 'POST', body: JSON.stringify({ enabled }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/**
 * The merchant's answer to "does another tag already send this channel from the
 * browser". The only oracle for what we cannot observe: Shopify's native sales
 * channels ship their pixels in an isolated sandbox, invisible from our theme
 * embed and unreachable by our stamping.
 */
export function useDeclareFunnelTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (answers: Partial<Record<FunnelChannelKey, boolean | null>>) =>
      fetchApi('/onboarding/funnel-tags', { method: 'POST', body: JSON.stringify(answers) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

// =============================================================================
// BILLING
// =============================================================================

interface PlanStatus {
  plan: string;
  // Entitled tier: the raw plan when billing is active, FREE otherwise.
  effectivePlan: string;
  ordersUsed: number;
  ordersLimit: number;
  ordersRemaining: number;
  usagePercentage: number;
  isOverLimit: boolean;
  isNearLimit: boolean;
  trialActive: boolean;
  trialDaysRemaining: number;
  billingActive: boolean;
  upgradeRequired: boolean;
  message?: string;
}

interface SubscriptionStatus {
  active: boolean;
  plan: string;
  /** The cadence the live subscription is actually on. */
  interval: 'MONTHLY' | 'ANNUAL';
  billingId: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** A downgrade Shopify holds back to the next billing cycle. `plan` above is
   *  still the tier being paid for and served; this is what it becomes, and
   *  when. Null when nothing is scheduled. */
  pendingPlan: string | null;
  pendingPlanAt: string | null;
}

interface UpgradeRecommendation {
  recommendedPlan: string;
  reason: string;
  savings?: string;
  features: string[];
}

interface BillingStatusResponse {
  plan: PlanStatus;
  subscription: SubscriptionStatus;
  recommendation: UpgradeRecommendation | null;
}

interface BillingPlan {
  id: string;
  name: string;
  price: number;
  /** ISO 4217 code the price is expressed in. The API no longer sends a
   *  pre-formatted string: that is how the euro sign travelled to a client
   *  that had no idea what was actually being charged. */
  currency: string;
  /** Whole-year amount, and what that works out to per month. Both come from
   *  the API: re-deriving the discount client-side is how a page ends up
   *  quoting a number the charge does not match. */
  annualPrice: number;
  annualPricePerMonth: number;
  trialDays: number;
  ordersPerMonth: string;
  features: string[];
}

interface UsageResponse {
  events: {
    used: number;
    limit: number | 'Unlimited';
    remaining: number | 'Unlimited';
    percentage: number;
  };
  warnings: {
    isNearLimit: boolean;
    isOverLimit: boolean;
    message?: string;
  };
  trial: {
    active: boolean;
    daysRemaining: number;
  };
}

export function useBillingStatus() {
  return useQuery<BillingStatusResponse>({
    queryKey: ['billing', 'status'],
    queryFn: () => fetchApi('/billing/status'),
  });
}

export function useBillingPlans() {
  return useQuery<{ plans: BillingPlan[]; currency: string; annualDiscountPercent: number }>({
    queryKey: ['billing', 'plans'],
    queryFn: () => fetchApi('/billing/plans'),
  });
}

export function useBillingUsage() {
  return useQuery<UsageResponse>({
    queryKey: ['billing', 'usage'],
    queryFn: () => fetchApi('/billing/usage'),
  });
}

export function useSubscribe() {
  return useMutation({
    // referralCode is the partner code the merchant typed. Shopify has no promo
    // field of its own in app billing, so this is the ONLY way a code can reach
    // appSubscriptionCreate - without it the whole Shopify half of the partner
    // programme is unreachable, however well the backend handles it.
    mutationFn: ({ plan, referralCode, interval }: { plan: string; referralCode?: string; interval?: BillingIntervalValue }) =>
      fetchApi<{ success: boolean; confirmationUrl: string }>('/billing/subscribe', {
        method: 'POST',
        body: JSON.stringify({ plan, ...(referralCode ? { referralCode } : {}), ...(interval ? { interval } : {}) }),
      }),
  });
}

/**
 * The billing cadence. Sent on every plan change, never inferred server-side
 * from the previous one when the merchant has a toggle in front of them: the
 * button they pressed said "annual", so the request has to say annual too.
 */
export type BillingIntervalValue = 'MONTHLY' | 'ANNUAL';

export function useUpgradePlan() {
  return useMutation({
    mutationFn: ({ plan, interval }: { plan: string; interval?: BillingIntervalValue }) =>
      fetchApi<{ success: boolean; confirmationUrl?: string; message?: string }>('/billing/upgrade', {
        method: 'POST',
        body: JSON.stringify({ plan, ...(interval ? { interval } : {}) }),
      }),
  });
}

export function useDowngradePlan() {
  return useMutation({
    mutationFn: ({ plan, interval }: { plan: string; interval?: BillingIntervalValue }) =>
      fetchApi<{ success: boolean; confirmationUrl: string; message?: string }>('/billing/downgrade', {
        method: 'POST',
        body: JSON.stringify({ plan, ...(interval ? { interval } : {}) }),
      }),
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchApi('/billing/cancel', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useBillingHistory() {
  return useQuery<{ history: Array<{ date: string; type: string; amount: number; status: string }> }>({
    queryKey: ['billing', 'history'],
    queryFn: () => fetchApi('/billing/history'),
    staleTime: 60000,
  });
}

// =============================================================================
// WOOCOMMERCE BILLING (Stripe) — used by the hosted WC dashboard
// =============================================================================

export function useWooBillingStatus() {
  return useQuery<{ plan: string; hasSubscription: boolean }>({
    queryKey: ['woo-billing', 'status'],
    queryFn: () => fetchApi('/woo/billing/status'),
    staleTime: 30000,
  });
}

export function useWooBillingCheckout() {
  return useMutation({
    mutationFn: (plan: string) =>
      fetchApi<{ url: string }>('/woo/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ plan }),
      }),
  });
}

export function useWooBillingPortal() {
  return useMutation({
    mutationFn: () => fetchApi<{ url: string }>('/woo/billing/portal', { method: 'POST' }),
  });
}
